'use client';

import cs from 'classnames';
import { css, styled } from 'styled-components';

import Link from '@dvd/components/resource-link';
import type { CtaProps, SafeUrl } from '@types';
import { trackLink } from '@lib/tracking';

type TextLinkVariant = 'anchor' | 'link' | 'external';

type TextLinkProps = {
  /**
   * CTA props
   */
  cta: CtaProps;
  /**
   * Optional class name, nesc. for extending as a styled-component
   */
  className?: string;
  /**
   * Optional title for the link
   */
  title?: string;
  /**
   * Optional click event handler, using the same signature as
   * next/link
   */
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

/**
 * Determines link styling using fastest-first checks:
 * 1. Simple '#' check for page anchors (↓)
 * 2. Absolute URLs: external vs wistia links (↗ vs →)
 * 3. Relative URLs and invalid URLs default to internal (→)
 */
const computedVariant = (href: SafeUrl): TextLinkVariant => {
  if (href.startsWith('#')) return 'anchor';
  if (!href.startsWith('http')) return 'link';

  try {
    const url = new URL(href);
    return url.hostname.includes('wistia') ? 'link' : 'external';
  } catch {
    return 'link';
  }
};

export const TextLinkStyles = css`
  color: var(--global-link-color);
  text-decoration: underline transparent;
  transition: text-decoration-color var(--global-motion);

  &::after {
    content: '→';
    display: inline-block;
    padding-inline-start: ${({ theme }) => theme.spacing['0.25']};
    transition: transform var(--global-motion);
  }
  &.anchor::after {
    content: '↓';
  }
  &.external::after {
    content: '↗';
    transform: translate3d(0, 0.1875em, 0);
  }

  &:visited {
    color: var(--global-visited-color);
  }
`;

export const TextLinkHoverStyles = css`
  color: var(--global-hover-color);
  text-decoration: underline currentcolor;

  &::after {
    transform: translateX(${({ theme }) => theme.spacing['0.25']});
  }
  &.anchor::after {
    transform: translateY(${({ theme }) => theme.spacing['0.25']});
  }
  // custom transform for external links to move the arrow up and to the right
  &.external::after {
    transform: translate3d(0.125em, -0.0625em, 0);
  }
`;

const TextLinkWrapper = styled(Link)`
  ${TextLinkStyles}

  &:hover,
  &:active {
    ${TextLinkHoverStyles}
  }
`;

const handleLinkClick = (event, trackingLabel, trackingProperty) => {
  if (trackingLabel || trackingProperty) {
    trackLink(trackingLabel, trackingProperty);
  }
};

const TextLink = ({ cta, className, title, onClick }: TextLinkProps) => {
  const variant = computedVariant(cta.href);
  return (
    <TextLinkWrapper
      className={cs(className, variant)}
      href={cta.href}
      onClick={(event) => {
        handleLinkClick(event, cta.trackingLabel, cta.trackingProperty);
        onClick?.(event);
      }}
      rel={variant === 'external' ? 'external' : null}
      title={title}
    >
      {`${cta.text}`}
    </TextLinkWrapper>
  );
};

export default TextLink;
